/**
 * @file GemFM App - Login Page
 * @copyright Copyright (c) 2026 Woolly Bear Software LLC,
 *            all rights reserved.
 * @overview Login page for unauthenticated users.
 */
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import React from "react";

import * as constants from "../constants";
import { gemColors } from "../theme";
import { getRouterBase } from "../utils";
import { FeedbackDialog } from "./feedback-dialog";

/** Login component. */
function Login() {
  const [showFeedback, setShowFeedback] = React.useState(false);

  const handleLogin = () => {
    const location = `${getRouterBase()}login`;
    window.location.href = location;
  };

  return (
    <Container
      maxWidth="sm"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minHeight: "100vh",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          flexGrow: 1,
        }}
      >
        <Box sx={{ textAlign: "center", maxWidth: 500 }}>
          <Box
            component="img"
            src={`${constants.WEBSITE_URL}/images/unheard-fm-logo.png`}
            alt="Unheard.FM Logo"
            sx={{ mb: 1, height: 80, width: "auto" }}
          />
          <Typography
            variant="h1"
            component="h1"
            sx={{
              mb: 1,
              fontSize: "2rem",
              fontWeight: 500,
              textTransform: "uppercase",
              opacity: 0.8,
              fontFamily: "'Figtree', sans-serif",
            }}
          >
            Unheard.FM
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 4, fontSize: "1.1rem" }}>
            Transparent, unsponsored, unbiased music discovery.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={handleLogin}
            sx={{
              bgcolor: gemColors.lightSuccess,
              color: gemColors.black1,
              fontSize: "14px",
              fontWeight: 500,
              "&:hover": {
                bgcolor: "#7AAF73",
              },
            }}
            endIcon={
              <Box
                component="img"
                src={`${constants.WEBSITE_URL}/images/spotify-logo-black.png`}
                alt="Spotify Logo"
                sx={{ height: 23 }}
              />
            }
          >
            Log in with Spotify
          </Button>
        </Box>
      </Box>
      <Box sx={{ textAlign: "center", pt: 3, pb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          <Link
            href={`${constants.WEBSITE_URL}/privacy.html`}
            color="text.secondary"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy
          </Link>
          {" | "}
          <Link
            href={`${constants.WEBSITE_URL}/terms.html`}
            color="text.secondary"
            target="_blank"
            rel="noopener noreferrer"
          >
            Terms
          </Link>
          {" | "}
          <Link
            component="button"
            type="button"
            onClick={() => setShowFeedback(true)}
            color="text.secondary"
            sx={{ verticalAlign: "baseline" }}
          >
            Feedback
          </Link>
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: "0.7rem" }}
        >
          &copy; 2026 Woolly Bear Software LLC. All rights reserved.
        </Typography>
      </Box>
      <FeedbackDialog
        open={showFeedback}
        onClose={() => setShowFeedback(false)}
      />
    </Container>
  );
}

export { Login };
